import { useEffect, useRef, useState } from "react";
import { motion, useScroll, useSpring, useMotionValueEvent } from "framer-motion";
import { milestones } from "@/data/content";

const BLUE = "#1d4ed8";
const YELLOW = "#facc15";
const INK = "#0f172a";

const VB_W = 1000;
const VB_H = 1920;
const ROW_H = 320;

const ROUTE =
    "M 500 20 C 500 90, 360 90, 360 160 C 360 300, 640 340, 640 480 C 640 620, 360 660, 360 800 C 360 940, 640 980, 640 1120 C 640 1260, 360 1300, 360 1440 C 360 1580, 640 1620, 640 1760";

const NODES = [0, 1, 2, 3, 4, 5].map((i) => ({
    x: i % 2 === 0 ? 360 : 640,
    y: 160 + i * ROW_H,
}));

const HUB_META = [
    { title: "Incorporated in Gurgaon", caption: "HQ established · first dedicated FTL lane", img: "/assets/journey/2015.webp" },
    { title: "25 Fastest-Growing", caption: "New hubs join · Tier-1 supplier lanes", img: "/assets/journey/2017.webp" },
    { title: "Best Vendor · Sekisui DLJM", caption: "Rail-linked movement comes online", img: "/assets/journey/2019.webp" },
    { title: "Best Partner · Carrier", caption: "Air support for time-critical freight", img: "/assets/journey/2023.webp" },
    { title: "Repeat Best Partner", caption: "Two new branches deepen the web", img: "/assets/journey/2024.webp" },
    { title: "Customer Appreciation · Sakura", caption: "The mesh completes", img: "/assets/journey/2025.webp" },
];

const TruckArt = ({ wheelBack, wheelFront }) => (
    <svg viewBox="-20 -16 40 24" className="w-12 h-auto drop-shadow-md" style={{ overflow: "visible" }}>
        <g transform="translate(0 -4)">
            <rect x="-17" y="-13" width="21" height="12" rx="2" fill={BLUE} />
            <rect x="-17" y="-6" width="21" height="2.4" fill={YELLOW} />
            <path d="M 5 -10 h 7 a 3 3 0 0 1 2.6 1.6 L 17 -4 v 3 h -12 Z" fill="#3b82f6" />
            <rect x="9" y="-8.5" width="4.5" height="3.6" rx="0.8" fill="#dbeafe" />
            <g transform="translate(-10 0)">
                <g ref={wheelBack}>
                    <circle r="3.4" fill={INK} />
                    <circle r="1.3" fill="#94a3b8" />
                    <line x1="-3" y1="0" x2="3" y2="0" stroke="#64748b" strokeWidth="0.7" />
                </g>
            </g>
            <g transform="translate(10 0)">
                <g ref={wheelFront}>
                    <circle r="3.4" fill={INK} />
                    <circle r="1.3" fill="#94a3b8" />
                    <line x1="-3" y1="0" x2="3" y2="0" stroke="#64748b" strokeWidth="0.7" />
                </g>
            </g>
        </g>
    </svg>
);

const MilestoneCard = ({ m, meta, reached, side, index }) => (
    <motion.div
        initial={{ opacity: 0, x: side === "right" ? 44 : -44 }}
        whileInView={{ opacity: 1, x: 0 }}
        viewport={{ once: true, amount: 0.35 }}
        transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
        data-testid={`journey-card-${m.year}`}
        className={`rounded-2xl bg-white overflow-hidden transition-all duration-500 ${
            reached ? "ring-2 ring-blue-600/35 shadow-xl -translate-y-0.5" : "ring-1 ring-slate-200 shadow-md"
        }`}
    >
        <div className="relative h-36 overflow-hidden bg-slate-50">
            <img src={meta.img} alt={`Beeta in ${m.year}`} loading={index > 1 ? "lazy" : undefined} className="w-full h-full object-cover" />
            <div className="absolute top-3 left-3 px-2.5 py-1 rounded-full bg-slate-900/85 backdrop-blur text-brand-yellow text-[10px] font-bold uppercase tracking-[0.18em]">
                {m.year}
            </div>
            <div
                className={`absolute inset-x-0 bottom-0 h-1 bg-gradient-to-r from-blue-600 to-brand-yellow origin-left transition-transform duration-700 ${
                    reached ? "scale-x-100" : "scale-x-0"
                }`}
            />
        </div>
        <div className="p-5">
            <div className="font-display text-lg font-black text-slate-900 tracking-tight leading-snug">
                {meta.title}
            </div>
            <p className="mt-1.5 text-sm text-slate-600 leading-relaxed">{m.label}</p>
            <div className="mt-3 inline-flex items-center gap-2 text-[10px] font-bold uppercase tracking-[0.15em] text-blue-600">
                <span className="w-5 h-px bg-blue-600" />
                {meta.caption}
            </div>
        </div>
    </motion.div>
);

export const JourneyNetwork = () => {
    const wrapRef = useRef(null);
    const pathRef = useRef(null);
    const drawRef = useRef(null);
    const truckRef = useRef(null);
    const wheelBack = useRef(null);
    const wheelFront = useRef(null);
    const lenRef = useRef(0);
    const nodeFracRef = useRef([]);
    const [stage, setStage] = useState(0);

    const { scrollYProgress } = useScroll({
        target: wrapRef,
        offset: ["start 0.75", "end 0.55"],
    });
    const prog = useSpring(scrollYProgress, { stiffness: 55, damping: 17, mass: 0.5 });

    const place = (t) => {
        const L = lenRef.current;
        if (!L || !pathRef.current || !wrapRef.current || !truckRef.current) return;
        const d = Math.max(0.5, Math.min(L - 0.5, t * L));
        const p = pathRef.current.getPointAtLength(d);
        const p2 = pathRef.current.getPointAtLength(Math.min(L, d + 2));
        const rect = wrapRef.current.getBoundingClientRect();
        const sx = rect.width / VB_W;
        const sy = rect.height / VB_H;
        const dx = (p2.x - p.x) * sx;
        const dy = (p2.y - p.y) * sy;
        let ang = (Math.atan2(dy, dx) * 180) / Math.PI;
        const flip = Math.abs(ang) > 90;
        if (flip) ang -= 180;
        const el = truckRef.current;
        el.style.left = `${(p.x / VB_W) * 100}%`;
        el.style.top = `${(p.y / VB_H) * 100}%`;
        el.style.transform = `translate(-50%, -55%) rotate(${ang}deg) scaleX(${flip ? -1 : 1})`;
        const spin = `rotate(${(d * 4) % 360})`;
        wheelBack.current?.setAttribute("transform", spin);
        wheelFront.current?.setAttribute("transform", spin);
        if (drawRef.current) drawRef.current.style.strokeDashoffset = Math.max(0, L * (1 - t));
        let s = 0;
        nodeFracRef.current.forEach((f) => {
            if (t >= f - 0.015) s += 1;
        });
        setStage(s);
    };

    useEffect(() => {
        const L = pathRef.current.getTotalLength();
        lenRef.current = L;
        const best = NODES.map(() => ({ dd: Infinity, len: 0 }));
        for (let i = 0; i <= 700; i++) {
            const len = (L * i) / 700;
            const p = pathRef.current.getPointAtLength(len);
            NODES.forEach((n, k) => {
                const dd = (p.x - n.x) ** 2 + (p.y - n.y) ** 2;
                if (dd < best[k].dd) {
                    best[k].dd = dd;
                    best[k].len = len;
                }
            });
        }
        nodeFracRef.current = best.map((b) => b.len / L);
        if (drawRef.current) {
            drawRef.current.style.strokeDasharray = L;
            drawRef.current.style.strokeDashoffset = L;
        }
        place(0);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useMotionValueEvent(prog, "change", (t) => place(t));

    return (
        <div className="pt-14 pb-20" data-testid="journey-network-section">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                {/* ===== FINALE (moved to top) ===== */}
                <motion.div
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, amount: 0.4 }}
                    transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
                    className="mb-16 lg:mb-24 relative overflow-hidden rounded-3xl bg-white ring-1 ring-slate-200 shadow-xl"
                    data-testid="journey-final-message"
                >
                    <div className="p-10 lg:p-14 text-center">
                        <div className="text-xs font-bold uppercase tracking-[0.2em] text-blue-600 mb-4">
                            2026 — Today
                        </div>
                        <h3 className="font-display text-3xl sm:text-4xl font-black tracking-tighter leading-tight text-slate-900">
                            One Connected Logistics <span className="text-blue-600">Ecosystem</span>
                        </h3>
                        <p className="mt-4 text-slate-600 leading-relaxed">
                            Beeta didn&apos;t simply grow older.{" "}
                            <span className="font-semibold text-slate-900">It became more connected.</span>
                        </p>
                    </div>
                </motion.div>

                {/* ===== DESKTOP: route + truck + alternating cards ===== */}
                <div ref={wrapRef} className="relative hidden lg:block" style={{ height: `${VB_H * 0.62}px` }}>
                    {/* route SVG (stretched; strokes stay uniform) */}
                    <svg
                        viewBox={`0 0 ${VB_W} ${VB_H}`}
                        preserveAspectRatio="none"
                        className="absolute inset-0 w-full h-full pointer-events-none"
                        data-testid="journey-route-svg"
                    >
                        <path
                            d={ROUTE}
                            fill="none"
                            stroke="#e2e8f0"
                            strokeWidth="2"
                            strokeDasharray="2 9"
                            vectorEffect="non-scaling-stroke"
                        />
                        <path
                            ref={drawRef}
                            d={ROUTE}
                            fill="none"
                            stroke={BLUE}
                            strokeWidth="3"
                            strokeLinecap="round"
                            vectorEffect="non-scaling-stroke"
                        />
                        <path ref={pathRef} d={ROUTE} fill="none" stroke="none" />
                        {/* connectors card ↔ node */}
                        {NODES.map((n, i) => (
                            <line
                                key={i}
                                x1={n.x}
                                y1={n.y}
                                x2={i % 2 === 0 ? 520 : 480}
                                y2={n.y}
                                stroke="#cbd5e1"
                                strokeWidth="1.5"
                                strokeDasharray="3 5"
                                vectorEffect="non-scaling-stroke"
                            />
                        ))}
                    </svg>

                    {/* milestone nodes (HTML — no distortion) */}
                    {NODES.map((n, i) => {
                        const reached = stage > i;
                        return (
                            <div
                                key={i}
                                className="absolute -translate-x-1/2 -translate-y-1/2 z-10"
                                style={{ left: `${(n.x / VB_W) * 100}%`, top: `${(n.y / VB_H) * 100}%` }}
                                data-testid={`journey-node-${milestones[i].year}`}
                            >
                                {reached && (
                                    <span className="absolute inset-0 rounded-full bg-blue-500 animate-ping opacity-30" />
                                )}
                                <div
                                    className={`relative w-7 h-7 rounded-full flex items-center justify-center ring-4 ring-white shadow-md transition-colors duration-500 ${
                                        reached ? "bg-blue-600" : "bg-slate-300"
                                    }`}
                                >
                                    <span className={`w-2 h-2 rounded-full ${reached ? "bg-brand-yellow" : "bg-white"}`} />
                                </div>
                                <div
                                    className={`absolute left-1/2 -translate-x-1/2 top-9 font-display font-black text-sm tracking-tight transition-colors duration-500 ${
                                        reached ? "text-blue-700" : "text-slate-400"
                                    }`}
                                >
                                    {milestones[i].year}
                                </div>
                            </div>
                        );
                    })}

                    {/* truck overlay */}
                    <div
                        ref={truckRef}
                        className="absolute z-20 will-change-transform"
                        style={{ left: "50%", top: "0%" }}
                        data-testid="journey-truck"
                    >
                        <TruckArt wheelBack={wheelBack} wheelFront={wheelFront} />
                    </div>

                    {/* alternating cards */}
                    {milestones.map((m, i) => {
                        const side = i % 2 === 0 ? "right" : "left";
                        return (
                            <div
                                key={m.year}
                                className="absolute w-[38%]"
                                style={{
                                    top: `${((160 + i * ROW_H) / VB_H) * 100}%`,
                                    transform: "translateY(-50%)",
                                    ...(side === "right" ? { left: "54%" } : { left: "8%" }),
                                }}
                            >
                                <MilestoneCard m={m} meta={HUB_META[i]} reached={stage > i} side={side} index={i} />
                            </div>
                        );
                    })}
                </div>

                {/* ===== MOBILE: stacked cards on a simple spine ===== */}
                <div className="lg:hidden relative pl-9">
                    <div className="absolute left-3 top-2 bottom-2 w-px bg-slate-200" />
                    {milestones.map((m, i) => (
                        <div key={m.year} className="relative pb-8 last:pb-0">
                            <div className="absolute -left-9 top-2 w-6 h-6 rounded-full bg-blue-600 ring-4 ring-white shadow flex items-center justify-center translate-x-0.5">
                                <span className="w-1.5 h-1.5 rounded-full bg-brand-yellow" />
                            </div>
                            <MilestoneCard m={m} meta={HUB_META[i]} reached side="left" index={i} />
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
};

export default JourneyNetwork;
